/**
 * @file VerifyEmailForm.jsx
 * Component for verifying user email during registration.
 * It includes input fields for email and OTP, handles form submission,
 * It provides UI for sending OTP and verifying it.
 */

import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
    Button,
    TextField,
    FormLabel,
    Tooltip,
    tooltipClasses,
    CircularProgress
} from '@mui/material';
import OtpInput from 'react18-input-otp';
import useAuthNavigation from '../useAuthNavigation';
import useVerifyEmail from './hooks/useVerifyEmail';
import { Body2, Body3, H3 } from '../../../themes/typography';
import '../../../styles/components/LoginForm.css';

export default function VerifyEmailForm() {
    const { loading, email } = useSelector((state) => state.auth);
    const { showLoginForm } = useAuthNavigation();
    const {
        formik,
        seconds,
        getSlideClass,
        resendLoading,
        activeSlide,
        setActiveSlide,
        resendOtpEmail,
        verifyWithOtp,
        verifyEmail
    } = useVerifyEmail();

    return (
        <React.Fragment>
            <div className="main-login-email-collect-container">
                <div className="login-welcome-greetings">
                    <H3 color="var(--secondary-color)" fontWeight={700}>
                        Sign up
                    </H3>
                    <Body2 color="var(--tertiary-color)">
                        {activeSlide === 'slide2'
                            ? `Sign up as ${email}`
                            : 'Enter your email to verify your account'}
                    </Body2>
                </div>

                <form className="main-login-form" autoComplete="off" onSubmit={formik.handleSubmit}>
                    <div
                        className={`forgot-password-method-container slide-transition ${getSlideClass('slide1')}`}
                    >
                        <div className="main-switchable-login-form">
                            <div className="switchable-login-input-container">
                                <TextField
                                    fullWidth
                                    id="verifyEmail"
                                    name="verifyEmail"
                                    label="Email"
                                    value={formik.values.email}
                                    disabled={loading}
                                    onChange={(e) => formik.setFieldValue('email', e.target.value)}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                />
                            </div>

                            <div className="login-methods-container">
                                <div className="login-methods-action-buttons">
                                    <Button
                                        variant="main"
                                        fullWidth
                                        type="submit"
                                        disabled={loading}
                                        onClick={verifyEmail}
                                    >
                                        Send OTP
                                        {loading && (
                                            <CircularProgress size={20} sx={circularProgressBar} />
                                        )}
                                    </Button>
                                </div>
                                <div
                                    className="dont-have-login-account"
                                    style={{
                                        justifyContent: 'center',
                                        paddingTop: '0.75rem',
                                        borderTop: `1px solid var(--divider-color)`
                                    }}
                                >
                                    <Body3>Already have an account?</Body3>
                                    <Button
                                        sx={guestLoginBtnStyle}
                                        disableRipple
                                        onClick={showLoginForm}
                                    >
                                        Sign in
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        className={`send-login-method-container slide-transition ${getSlideClass('slide2')}`}
                    >
                        <div className="main-switchable-login-form">
                            <div className="switchable-login-input-container">
                                <FormLabel>
                                    <Body2 color="var(--secondary-color)">
                                        Enter the OTP sent to your email
                                    </Body2>
                                </FormLabel>
                                <div
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.5rem'
                                    }}
                                >
                                    <OtpInput
                                        id="otp"
                                        name="otp"
                                        isInputNum
                                        // isInputSecure
                                        // isDisabled
                                        value={formik.values.otp}
                                        onChange={(otpNumber) =>
                                            formik.setFieldValue('otp', otpNumber)
                                        }
                                        numInputs={6}
                                        inputStyle={otpInputStyle}
                                        containerStyle={{
                                            justifyContent: 'space-between',
                                            gap: '1rem'
                                        }}
                                        focusStyle={{
                                            outline: 'none',
                                            border: `2px solid var(--secondary-color)`
                                        }}
                                        errorStyle={{ border: `2px solid var(--error-main-color)` }}
                                        hasErrored={
                                            formik.touched.otp && Boolean(formik.errors.otp)
                                        }
                                    />
                                    <Body3 color="var(--error-main-color)" fontWeight={300}>
                                        {formik.touched.otp && formik.errors.otp}
                                    </Body3>
                                </div>
                                <Tooltip
                                    title="If you haven't received an email, please check your spam folder."
                                    placement="bottom-start"
                                    slotProps={{
                                        tooltip: {
                                            sx: {
                                                maxWidth: '332px',
                                                fontWeight: '400',
                                                fontFamily: 'var(--secondary-content-font-family)',
                                                color: 'var(--primary-color)',
                                                backgroundColor: 'var(--quaternary-color)',
                                                margin: '0',
                                                fontSize: '0.8rem'
                                            }
                                        },
                                        popper: {
                                            sx: {
                                                [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                                                    {
                                                        marginTop: '0.25rem'
                                                    }
                                            }
                                        }
                                    }}
                                >
                                    <div className="dont-have-login-account">
                                        <Body3>Didn't receive an email?</Body3>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0rem'
                                            }}
                                        >
                                            <Button
                                                onClick={resendOtpEmail}
                                                disabled={resendLoading || seconds > 0}
                                                sx={{
                                                    ...guestLoginBtnStyle,
                                                    '&.Mui-disabled': {
                                                        background: 'transparent',
                                                        color: '#26c1e8'
                                                    }
                                                }}
                                            >
                                                Resend
                                            </Button>
                                            {seconds > 0 && (
                                                <Body3
                                                    color="var(--tertiary-color)"
                                                    fontWeight={500}
                                                >
                                                    {`00:${seconds < 10 ? `0${seconds}` : seconds}`}
                                                </Body3>
                                            )}
                                        </div>
                                    </div>
                                </Tooltip>
                            </div>

                            <div className="login-methods-container">
                                <div className="login-methods-action-buttons">
                                    <Button
                                        variant="main"
                                        fullWidth
                                        type="submit"
                                        disabled={loading}
                                        onClick={verifyWithOtp}
                                    >
                                        Continue{' '}
                                        {loading && (
                                            <CircularProgress
                                                size={20}
                                                sx={{
                                                    marginLeft: '0.75rem',
                                                    color: 'var(--primary-color)'
                                                }}
                                            />
                                        )}
                                    </Button>
                                    <div className="guest-login-option">
                                        <Button
                                            onClick={() => setActiveSlide('slide1')}
                                            disableRipple
                                            sx={backToOption}
                                        >
                                            Back to Sign up
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                <div className="login-terms-agree-container">
                    <Body3>
                        By signing in you accept Shmooz {''}
                        <span className="terms">
                            <Link>Terms of Service</Link>
                        </span>{' '}
                        and
                        <span className="terms">
                            {' '}
                            {''}
                            <Link>Privacy Policy</Link>
                        </span>
                    </Body3>
                </div>
            </div>
        </React.Fragment>
    );
}

const circularProgressBar = {
    color: 'var(--primary-color)',
    marginLeft: '0.75rem'
};

const guestLoginBtnStyle = {
    padding: '0',
    fontSize: '0.875rem',
    color: 'var(--tertiary-color)',
    fontWeight: '500',
    fontFamily: 'var(--secondary-content-font-family)',
    minWidth: '56px'
};

const backToOption = {
    padding: '0',
    fontSize: '0.875rem',
    color: '#ccc',
    '&:hover': {
        textDecoration: 'underline'
    }
};

const otpInputStyle = {
    width: '100%',
    margin: '0px',
    padding: '12px',
    color: '#f5f5f5',
    border: `1px solid #f5f5f5`,
    backgroundColor: 'transparent',
    fontWeight: 500,
    fontSize: '1rem',
    borderRadius: '10px'
};
